import { FC } from 'react';
import Moment from 'react-moment';
import { useTranslation } from 'react-i18next';
import {
  Chip,
  Avatar,
  Box,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import useNavigate from '../../../hooks/useNavigate';
import STRING_KEYS from '../../../language/keys';
import { OfficeBuilding } from '../../../icons/office-building';
import { DATE_FORMAT } from '../../../consts';
import { POSITION_PARAMS } from '../../../consts/requestParamsKeys';
import { Sort } from '../../../interfaces/apiRequest.interface';
import { CustomTooltip } from '../../../components/CustomTooltip';
import TableContainer from '../../../components/Table/TableContainer';
import TableHeadContainer from '../../../components/Table/TableHead';
import TableCountRow from '../../../components/Table/TableCountRow';
import PositionAction from '../PositionActions';
import PositionStatus from '../../../components/PositionStatus';
import PositionLocation from '../../../components/PositionDetails/position-location/position-location.component';
import { Position, PositionState } from '../../../@types/position';
import style from './style';

interface PositionListTableProps {
  positions: Position[];
  onDeleteHandler: (positionId: string, accountId: string) => void
  onClosePositionHandler: (positionId: string, accountId: string, positionState: PositionState) => void
  onPageChange: (newPageNumber: number) => void
  page: number;
  totalCount: number;
  isLoading: boolean;
  order: Sort;
  orderHandler: (val: Sort) => void
}

const PositionTable: FC<PositionListTableProps> = (props) => {
  const {
    positions,
    onPageChange,
    page,
    totalCount,
    isLoading,
    order,
    orderHandler,
    onDeleteHandler,
    onClosePositionHandler,
  } = props;
  const { t: translate } = useTranslation();
  const { goToPositionDetails } = useNavigate();
  const headers = [
    {
      key: POSITION_PARAMS.JOB_TITLE,
      value: translate(STRING_KEYS.POSITIONS_LIST_TABLE_HEADER_TITLE),
      isSortable: true,
    },
    {
      key: POSITION_PARAMS.ACCOUNT_NAME,
      value: translate(STRING_KEYS.POSITIONS_LIST_TABLE_HEADER_ACCOUNT),
    },
    {
      key: POSITION_PARAMS.CITY,
      value: translate(STRING_KEYS.POSITIONS_LIST_TABLE_HEADER_LOCATION),
      isSortable: true,
    },
    {
      key: POSITION_PARAMS.MATCHES,
      value: translate(STRING_KEYS.POSITIONS_LIST_TABLE_HEADER_MATCHES),
    },
    {
      key: POSITION_PARAMS.STATUS,
      value: translate(STRING_KEYS.POSITIONS_LIST_TABLE_HEADER_STATUS),
      isSortable: true,
    },
    {
      key: POSITION_PARAMS.UPDATE_AT,
      value: translate(STRING_KEYS.POSITIONS_LIST_TABLE_HEADER_UPDATED),
      isSortable: true,
    },
    {
      key: POSITION_PARAMS.CREATED_AT,
      value: translate(STRING_KEYS.POSITIONS_LIST_TABLE_HEADER_CREATED),
      isSortable: true,
    },
    {
      key: 'action',
      value: '',
    },
  ];

  const renderTableBody = () => {
    return positions.map((position) => {
      return (
        <TableRow
          hover
          key={ position.id }
          onClick={ () => goToPositionDetails(position.id) }
        >
          <TableCell>
            <Box sx={ style.tableColumn.box.sx }>
              <Avatar
                imgProps={ {
                  style: { objectFit: 'contain' },
                } }
                variant={ style.tableColumn.avatar.variant }
                sx={ style.tableColumn.avatar.sx }
                src={ position.accountLogo }
              >
                <OfficeBuilding />
              </Avatar>
              <Box sx={ style.tableColumn.rowBox.sx }>
                <CustomTooltip title={ position.jobTitle }>
                  <Typography
                    noWrap
                    variant={ style.tableColumn.rowBox.textFirst.variant }
                  >
                    {position.jobTitle}
                  </Typography>
                </CustomTooltip>
                <CustomTooltip title={ position.id }>
                  <Typography
                    noWrap
                    color={ style.tableColumn.rowBox.textSecond.color }
                    variant={ style.tableColumn.rowBox.textSecond.variant }
                  >
                    {position.id}
                  </Typography>
                </CustomTooltip>
              </Box>

            </Box>
          </TableCell>
          <TableCell>
            <Box sx={ style.tableColumn.rowBox.sx }>
              {position.accountName && (
                <CustomTooltip title={ position.accountName }>
                  <Typography
                    noWrap
                    variant={ style.tableColumn.rowBox.textFirst.variant }
                  >
                    {position.accountName}
                  </Typography>
                </CustomTooltip>
              )}
              <CustomTooltip title={ position.companyName }>
                <Typography
                  noWrap
                  color={ style.tableColumn.rowBox.textSecond.color }
                  variant={ style.tableColumn.rowBox.textSecond.variant }
                >
                  {position.companyName}
                </Typography>
              </CustomTooltip>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={ style.tableColumn.rowBox.sx }>
              <PositionLocation position={ position } />
            </Box>
          </TableCell>
          <TableCell>
            <Chip
              sx={ style.tableColumn.matchChip.sx }
              label={ position.numberOfMatches }
              variant={ style.tableColumn.chip.variant }
            />
          </TableCell>
          <TableCell>
            <PositionStatus
              closedAt={ position?.positionClosure?.closedAt || '' }
              closedByUserId={ position?.positionClosure?.closedBy?.userId || '' }
              closureReason={ position?.positionClosure?.closureReason || '' }
              closureReasonfreeText={ position?.positionClosure?.freeText || '' }
              state={ position.state }
              status={ position.status }
              isNew={ false }
            />
          </TableCell>
          <TableCell>
            <Moment format={ DATE_FORMAT.TABLE_LIST }>
              {position.updatedAt}
            </Moment>
          </TableCell>
          <TableCell>
            <Moment format={ DATE_FORMAT.TABLE_LIST }>
              {position.createdAt}
            </Moment>
          </TableCell>
          <TableCell>
            <PositionAction
              positionId={ position.id }
              accountId={ position.accountId }
              onDeleteHandler={ onDeleteHandler }
              onClosePositionHandler={ onClosePositionHandler }
              positionState={ position.state }
            />
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <TableContainer
      totalCount={ totalCount }
      onPageChange={ (e, newPage) => {
        onPageChange(newPage);
      } }
      page={ page }
      isLoading={ isLoading }
    >
      <TableHeadContainer
        headers={ headers }
        order={ order }
        orderHandler={ orderHandler }
      />
      <TableBody>
        <TableCountRow
          count={ totalCount }
          text={ translate(STRING_KEYS.POSITION_TABLE_ROW_RESULT_COUNT) }
          colSpan={ headers.length }
        />
        { renderTableBody() }
      </TableBody>
    </TableContainer>
  );
};

export default PositionTable;
